import { ShieldCheck, Settings, BarChart3, TrendingUp, Cookie } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import { Reveal } from "@/components/Reveal";

export function Pillars() {
  const { t } = useTranslation();
  const items = [
    { Icon: ShieldCheck, title: t("pillars.obtain.title"), desc: t("pillars.obtain.desc"), step: "01" },
    { Icon: Settings, title: t("pillars.manage.title"), desc: t("pillars.manage.desc"), step: "02" },
    { Icon: BarChart3, title: t("pillars.optimize.title"), desc: t("pillars.optimize.desc"), step: "03" },
  ];

  // bar chart data (7 days)
  const bars = [42, 56, 48, 71, 64, 82, 76];
  const maxBar = Math.max(...bars);

  const categories = [
    { label: t("pillars.preview.necessary"), pct: 100, color: "bg-primary" },
    { label: t("pillars.preview.preferences"), pct: 78, color: "bg-primary/80" },
    { label: t("pillars.preview.statistics"), pct: 64, color: "bg-primary/60" },
    { label: t("pillars.preview.marketing"), pct: 41, color: "bg-primary/40" },
  ];

  return (
    <section className="py-20 md:py-28">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-6">
        {/* Centered header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t("pillars.eyebrow")}
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            {t("pillars.title")}
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            {t("pillars.subtitle")}
          </p>
        </div>

        {/* Two-column: analytics dashboard left, 3 pillars right */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT — analytics dashboard widget */}
          <Reveal>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-primary/10 via-transparent to-primary/5 blur-2xl"
              />

              {/* main card */}
              <div className="relative rounded-2xl border border-border bg-white shadow-[0_30px_60px_-30px_rgba(28,100,164,0.22)] p-6 md:p-7">
                {/* header row */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      {t("pillars.dash.title")}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t("pillars.dash.range")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 px-2 py-1 text-[11px] font-semibold">
                    <TrendingUp className="w-3 h-3" />
                    +12.4%
                  </div>
                </div>

                {/* big metric */}
                <div className="mt-5 flex items-end gap-3">
                  <div className="text-4xl md:text-5xl font-bold text-foreground tracking-tight leading-none">
                    84.3<span className="text-2xl text-muted-foreground">%</span>
                  </div>
                  <div className="pb-1.5 text-xs text-muted-foreground">
                    {t("pillars.dash.optInRate")}
                  </div>
                </div>

                {/* bar chart */}
                <div className="mt-6">
                  <div className="flex items-end gap-2 h-24">
                    {bars.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                        <div className="w-full flex-1 flex items-end">
                          <div
                            className={`w-full rounded-md transition-all ${
                              i === bars.length - 1 ? "bg-primary" : "bg-primary/25"
                            }`}
                            style={{ height: `${(v / maxBar) * 100}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {["M", "D", "M", "D", "F", "S", "S"][i]}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* category bars */}
                <div className="mt-6 pt-5 border-t border-border space-y-2.5">
                  {categories.map((c, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-[12px] mb-1">
                        <span className="text-foreground/80">{c.label}</span>
                        <span className="font-semibold text-foreground">{c.pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full ${c.color} rounded-full`}
                          style={{ width: `${c.pct}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* floating notification chip */}
              <div className="absolute -top-4 -right-3 hidden sm:flex items-center gap-2.5 rounded-xl border border-border bg-white px-3.5 py-2.5 shadow-lg max-w-[200px]">
                <div className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                  <Cookie className="w-3.5 h-3.5" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-foreground leading-tight">
                    {t("pillars.dash.scanTitle")}
                  </div>
                  <div className="text-[10px] text-muted-foreground leading-tight">
                    {t("pillars.dash.scanSub")}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — 3 pillars */}
          <div className="space-y-8">
            {items.map(({ Icon, title, desc, step }, i) => (
              <Reveal key={i} delay={i * 80} className="flex gap-5">
                <div className="shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-bold tracking-[0.18em] text-primary/70">
                    {step}
                  </div>
                  <h3 className="mt-1 text-lg font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
